import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { FaExclamationTriangle, FaList, FaAngleRight, FaExchangeAlt } from 'react-icons/fa';
import HolyLogo from '../components/HolyLogo';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const glowAnimation = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(108, 92, 231, 0.2), 0 0 20px rgba(108, 92, 231, 0.2);
  }
  50% {
    box-shadow: 0 0 20px rgba(108, 92, 231, 0.4), 0 0 50px rgba(108, 92, 231, 0.4);
  }
  100% {
    box-shadow: 0 0 5px rgba(108, 92, 231, 0.2), 0 0 20px rgba(108, 92, 231, 0.2);
  }
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 5rem;
  padding: 3rem 2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 500px;
    height: 500px;
    border-radius: 50%;
    background: ${props => props.theme.colors.gradient1};
    filter: blur(120px);
    opacity: 0.15;
    top: -250px;
    right: -100px;
    z-index: -1;
    animation: ${float} 7s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: ${props => props.theme.colors.gradient2};
    filter: blur(120px);
    opacity: 0.15;
    bottom: -200px;
    left: -100px;
    z-index: -1;
    animation: ${float} 9s ease-in-out infinite;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const HeroTagline = styled.div`
  display: inline-block;
  background: rgba(108, 92, 231, 0.1);
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  margin-bottom: 1.5rem;
  box-shadow: ${props => props.theme.boxShadow.small};
  border: 1px solid rgba(108, 92, 231, 0.2);
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  background: ${props => props.theme.colors.gradient1};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
  position: relative;
  letter-spacing: -0.02em;
  line-height: 1.2;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2.4rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 100px;
    height: 4px;
    background: ${props => props.theme.colors.gradient1};
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textMuted};
  margin-bottom: 2.8rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
`;

const FeaturesSection = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  margin-bottom: 5rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: ${props => props.theme.colors.gradient3};
    filter: blur(120px);
    opacity: 0.08;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: -1;
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1.8rem;
  position: relative;
  width: 75px;
  height: 75px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-left: auto;
  margin-right: auto;
  background: rgba(108, 92, 231, 0.1);
  color: ${props => props.theme.colors.primary};
  transition: ${props => props.theme.transition.default};
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    padding: 2px;
    background: ${props => props.gradient};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
  
  svg {
    font-size: 1.8rem;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.2rem;
  font-weight: 700;
  text-align: center;
  font-family: ${props => props.theme.fonts.headings};
  background: ${props => props.theme.colors.gradient1};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FeatureDescription = styled.p`
  color: ${props => props.theme.colors.textMuted};
  margin-bottom: 2rem;
  text-align: center;
  line-height: 1.7;
  font-size: 1.05rem;
`;

const CTASection = styled.section`
  text-align: center;
  background-color: rgba(108, 92, 231, 0.05);
  padding: 4.5rem 2rem;
  border-radius: 24px;
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.theme.boxShadow.medium};
  border: 1px solid rgba(108, 92, 231, 0.1);
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    top: -100px;
    left: -100px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: ${props => props.theme.colors.gradient1};
    opacity: 0.05;
    filter: blur(70px);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -100px;
    right: -100px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: ${props => props.theme.colors.gradient2};
    opacity: 0.05;
    filter: blur(70px);
  }
`;

const CTATitle = styled.h2`
  font-size: 2.4rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  position: relative;
  display: inline-block;
  font-family: ${props => props.theme.fonts.headings};
  
  &::after {
    content: '';
    position: absolute;
    width: 70%;
    height: 4px;
    background: ${props => props.theme.colors.gradient1};
    bottom: -10px;
    left: 15%;
    border-radius: 2px;
  }
`;

const CTADescription = styled.p`
  margin-bottom: 3rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  font-size: 1.15rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.textMuted};
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  position: relative;
  overflow: hidden;
  transition: ${props => props.theme.transition.default};
  background: rgba(26, 26, 26, 0.7);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(108, 92, 231, 0.1);
  
  &:hover {
    background: rgba(30, 30, 36, 0.9);
    border-color: rgba(108, 92, 231, 0.2);
  }
  
  &:hover ${FeatureIcon} {
    transform: scale(1.1);
    animation: ${glowAnimation} 2s infinite;
  }
`;

const Home = () => {
  return (
    <>
      <HeroSection>
        <HeroContent>
          <HeroTagline>
            <HolyLogo style={{ marginRight: '0.5rem', fontSize: '1.5rem' }} />
            Sécurité & Protection
          </HeroTagline>
          <HeroTitle>
            Bienvenue sur HolyHubs
          </HeroTitle>
          <HeroSubtitle>
            Une plateforme sécurisée pour la communauté Telegram HolyHubs permettant de signaler et vérifier 
            les scammers, protégeant ainsi nos membres contre les activités frauduleuses.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>
      
      <FeaturesSection>
        <StyledCard hoverable>
          <FeatureIcon gradient={props => props.theme.colors.gradient1}>
            <FaList />
          </FeatureIcon>
          <FeatureTitle>Scamlist</FeatureTitle>
          <FeatureDescription>
            Consultez une liste complète et à jour des scammers vérifiés dans notre communauté.
          </FeatureDescription>
          <Button as={Link} to="/scam-list" small>
            Voir la Liste <FaAngleRight />
          </Button>
        </StyledCard>
        
        <StyledCard hoverable>
          <FeatureIcon gradient={props => props.theme.colors.gradient3}>
            <FaExchangeAlt />
          </FeatureIcon>
          <FeatureTitle>Service d'Escrow</FeatureTitle>
          <FeatureDescription>
            Protégez vos transactions avec notre service d'escrow sécurisé et supervisé par nos administrateurs.
          </FeatureDescription>
          <Button as={Link} to="/escrow" variant="outline" small>
            Utiliser l'Escrow <FaAngleRight />
          </Button>
        </StyledCard>
        
        <StyledCard hoverable>
          <FeatureIcon gradient={props => props.theme.colors.gradient2}>
            <FaExclamationTriangle />
          </FeatureIcon>
          <FeatureTitle>Signaler des Scammers</FeatureTitle>
          <FeatureDescription>
            Signalez les individus qui ont tenté de vous arnaquer ou d'autres membres de la communauté.
          </FeatureDescription>
          <Button as={Link} to="/report" small>
            Signaler un Scammer <FaAngleRight />
          </Button>
        </StyledCard>
      </FeaturesSection>
      
      <CTASection>
        <CTATitle>Protégez Notre Communauté</CTATitle>
        <CTADescription>
          Aidez-nous à maintenir un environnement sûr pour tous les membres de HolyHubs en signalant les scammers 
          et en consultant la liste avant de vous engager dans des transactions.
        </CTADescription>
        <ButtonGroup>
          <Button as={Link} to="/scam-list" pulse>
            <FaList style={{ marginRight: '0.5rem' }} />
            Consulter la Scamlist
          </Button>
          <Button as={Link} to="/report" variant="secondary">
            <FaExclamationTriangle style={{ marginRight: '0.5rem' }} />
            Signaler un Scammer
          </Button>
        </ButtonGroup>
      </CTASection>
    </>
  );
};

export default Home; 